// eslint-disable-next-line no-use-before-define
import React, {
  ReactNode,
} from 'react';

import {
  Box, Flex, Grid, GridItem,
} from '@chakra-ui/react';
import Head from 'next/head';
import {
  IEvent, IEventTier, GetEventsDocument, IGetEventsQuery,
} from '../../lib/graphql';

import { initializeApollo } from '../../lib/apollo-client';
import EventEmptyState from '../../components/event/EventEmptyState';
import EventListCard from '../../components/event/EventListCard';
import SingleEventFeature from '../../components/event/SingleEventFeature';
import EventTicketPicker from '../../components/event/EventTicketPicker';
import BasketFooter from '../../components/basket/basketFooter';
import isMobile from '../../utils/isMobile';
import Hero from '../../components/hero/hero';

const apolloClient = initializeApollo();

interface IEventQueryResponse {
  events: Array<Pick<IEvent, 'id' | 'name' | 'image' | 'shortDescription' | 'startDateTime' | 'endDateTime'>
    &
    {
      // Lazy relation — see the note on the event detail page.
      tiers?: Pick<IEventTier, 'id' | 'name' | 'price' | 'availableSpaces' | 'maxSpaces'>[] | null
    }
  >
}

async function fetchEvents(name?: number):
Promise<IEventQueryResponse> {
  const query = await apolloClient.query({
    query: GetEventsDocument,
    variables: { data: { name } },
    // The Apollo client is created once at module load, so the default
    // cache-first policy pins the first result for the life of the process:
    // publishing or unpublishing an event would not appear until a restart,
    // and availability would be frozen at whatever it was on first render.
    fetchPolicy: 'no-cache',
  });
  const { getEvents }: IGetEventsQuery = query.data;
  const { events } = getEvents;

  return { events };
}

const EventsList = ({
  events,
}: IEventQueryResponse): ReactNode => {
  const live = events || [];

  // With one event a year, the listing spends most of its life holding zero or
  // one event. A three-column grid is right for neither, so the page picks a
  // shape from the count rather than always rendering a list.
  const single = live.length === 1 ? live[0] : null;

  return (
    <Flex direction="column">
      <Head>
        <title>{single ? single.name : 'Events'}</title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>

      {!single && <Hero title="Events" image="images/events/hero-dancer-group.jpeg" />}

      <Flex width="100%" direction="column" alignItems="center" py={single ? 0 : 10} px={[4, 6, 8]}>
        <Box width="100%" maxW={single ? '1200px' : '1280px'}>
          {/* No events — the common case between seasons. */}
          {live.length === 0 && <EventEmptyState />}

          {/* Exactly one event: show the event, not a list of one. */}
          {single && (
            <SingleEventFeature event={single}>
              <EventTicketPicker tiers={single.tiers} />
            </SingleEventFeature>
          )}

          {/* Several events: the grid earns its place, matching the course listing. */}
          {live.length > 1 && (
            <Grid
              gridTemplateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(2, 1fr)', 'repeat(3, 1fr)']}
              gap={[5, 6, 8]}
              alignItems="stretch"
            >
              {live.map((item) => (
                <GridItem key={`bookingList${item.id}`}>
                  <EventListCard event={item} />
                </GridItem>
              ))}
            </Grid>
          )}
        </Box>
      </Flex>

      {isMobile() && <BasketFooter />}
    </Flex>
  );
};

EventsList.getInitialProps = async () => {
  const { events } = await fetchEvents();

  return {
    events,
  };
};

export default EventsList;
